import React from 'react';
import { Box, Stack, Typography, TextField, Checkbox, FormControlLabel, IconButton, MenuItem, Button } from '@mui/material';
import { Delete, AddCircleOutline } from '@mui/icons-material';
import { useGetComplianceTypesQuery } from '../../../../services/DimensionService/dimensionApi';
import { EnumView } from '../../../../services/DimensionService/types/dimensionTypes';
import { ChecklistItemView } from '../../../../services/ComplianceService/types/complianceTypes';

interface ChecklistItemsListProps {
  items: ChecklistItemView[];
  onChange: (items: ChecklistItemView[]) => void;
  /** US3 add/remove customization mode; US2's round-completion view only toggles/annotates existing items. */
  editable?: boolean;
}

interface IndexedChecklistItem {
  item: ChecklistItemView;
  index: number;
}

const groupByCategory = (items: ChecklistItemView[]): Record<string, IndexedChecklistItem[]> => {
  const groups: Record<string, IndexedChecklistItem[]> = {};
  items.forEach((item, index) => {
    if (!groups[item.category]) {
      groups[item.category] = [];
    }
    groups[item.category].push({ item, index });
  });
  return groups;
};

const buildDisplayNameLookup = (options: EnumView[]): Record<string, string> =>
  Object.fromEntries(options.map(o => [o.value, o.name]));

interface ChecklistItemRowProps {
  item: ChecklistItemView;
  editable: boolean;
  onToggleCompliant: (isCompliant: boolean) => void;
  onNotesChange: (notes: string) => void;
  onRemove: () => void;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({ item, editable, onToggleCompliant, onNotesChange, onRemove }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start">
    <FormControlLabel
      sx={{ flex: 1 }}
      control={<Checkbox checked={item.isCompliant} onChange={(e) => onToggleCompliant(e.target.checked)} />}
      label={item.description}
    />
    <TextField
      label="Notes"
      size="small"
      fullWidth
      sx={{ flex: 1 }}
      value={item.notes}
      onChange={(e) => onNotesChange(e.target.value)}
    />
    {editable && (
      <IconButton size="small" aria-label={`Remove ${item.description}`} onClick={onRemove}>
        <Delete fontSize="small" />
      </IconButton>
    )}
  </Stack>
);

interface AddChecklistItemFormProps {
  categoryOptions: EnumView[];
  onAdd: (description: string, category: string) => void;
}

const AddChecklistItemForm: React.FC<AddChecklistItemFormProps> = ({ categoryOptions, onAdd }) => {
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState('');
  const canAdd = Boolean(description.trim() && category);

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd(description.trim(), category);
    setDescription('');
    setCategory('');
  };

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <TextField
        label="New Item Description"
        size="small"
        sx={{ flex: 2 }}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <TextField
        label="Category"
        size="small"
        select
        sx={{ flex: 1 }}
        value={category}
        onChange={(e) => setCategory(e.target.value)}
      >
        <MenuItem value="">
          <em>Select Category</em>
        </MenuItem>
        {categoryOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      <Button startIcon={<AddCircleOutline />} onClick={handleAdd} disabled={!canAdd} sx={{ textTransform: 'none' }}>
        Add Item
      </Button>
    </Stack>
  );
};

/**
 * ChecklistItemsList - renders an EOC round's line items grouped by category, each with a
 * compliant/not-compliant toggle and a notes field. Shared by US2 (recording round outcomes,
 * read/toggle only) and US3 (per-facility add/remove customization, `editable`).
 */
export const ChecklistItemsList: React.FC<ChecklistItemsListProps> = ({ items, onChange, editable = false }) => {
  const { data: complianceTypes = [] } = useGetComplianceTypesQuery();
  const categoryNames = React.useMemo(() => buildDisplayNameLookup(complianceTypes), [complianceTypes]);

  const updateItem = (index: number, updates: Partial<ChecklistItemView>) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addItem = (description: string, category: string) => {
    onChange([...items, { description, category, isCompliant: false, notes: '' }]);
  };

  const grouped = groupByCategory(items);
  const categories = Object.keys(grouped);

  if (categories.length === 0 && !editable) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {categories.map((category) => (
        <Box key={category}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            {categoryNames[category] || category}
          </Typography>
          <Stack spacing={1}>
            {grouped[category].map(({ item, index }) => (
              <ChecklistItemRow
                key={index}
                item={item}
                editable={editable}
                onToggleCompliant={(isCompliant) => updateItem(index, { isCompliant })}
                onNotesChange={(notes) => updateItem(index, { notes })}
                onRemove={() => removeItem(index)}
              />
            ))}
          </Stack>
        </Box>
      ))}

      {editable && <AddChecklistItemForm categoryOptions={complianceTypes} onAdd={addItem} />}
    </Stack>
  );
};

export default ChecklistItemsList;
